import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Button,
    Container,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    MenuSeparator,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import {
    EllipsisVerticalIcon,
    PencilIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { Link, router, usePage } from '@inertiajs/react';

const IndexView = () => {
    const { auth, calculations } = usePage().props;
    const { updateFilters, isSorted } = useFilters(['calculations']);
    const confirm = useConfirmationStore(state => state.confirm);

    const destroy = calculation => {
        router.delete(`/calculations/${calculation.id}`);
    };

    const confirmDeleteOrRestore = calculation => {
        confirm({
            description:
                'Weet u zeker dat u deze berekening wilt verwijderen? Alle gekoppelde informatie zal ook worden verwijderd.',
            onConfirm: close => {
                destroy(calculation);
                close();
            },
        });
    };

    return (
        <Container>
            <Segment>
                <SegmentHeader
                    header={`Berekeningen (${calculations?.meta?.total || 0})`}
                >
                    {auth.permissions.includes('create calculations') && (
                        <Button
                            variant='primary'
                            as={Link}
                            href='/calculations/create'
                        >
                            <PlusIcon className='size-5' />
                            Nieuwe berekening
                        </Button>
                    )}
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['calculations']} />

                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>UID</TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted('name')}
                                    onClick={() =>
                                        updateFilters('sort', 'name')
                                    }
                                >
                                    Naam
                                </TableHeaderCell>
                                <TableHeaderCell>Doelgroep</TableHeaderCell>
                                <TableHeaderCell />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {calculations?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={4} />
                            )}

                            {calculations?.data?.map(calculation => (
                                <TableRow key={calculation.id}>
                                    <TableCell>
                                        {calculation.unique_id}
                                    </TableCell>
                                    <TableCell>
                                        {auth?.permissions?.includes(
                                            'update calculations'
                                        ) ? (
                                            <Link
                                                href={`/calculations/${calculation.id}/edit`}
                                            >
                                                {calculation.name}
                                            </Link>
                                        ) : (
                                            calculation.name
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {calculation?.target_group?.name || ''}
                                    </TableCell>
                                    <TableCell
                                        className='w-px'
                                        singleLine
                                        textAlign='right'
                                    >
                                        {[
                                            'update calculations',
                                            'delete calculations',
                                        ]?.some(permission =>
                                            auth?.permissions?.includes(
                                                permission
                                            )
                                        ) && (
                                            <Menu>
                                                <MenuButton>
                                                    <EllipsisVerticalIcon className='size-5' />
                                                </MenuButton>
                                                <MenuItems anchor='bottom end'>
                                                    {auth?.permissions?.includes(
                                                        'update calculations'
                                                    ) && (
                                                        <>
                                                            <MenuItem
                                                                as={Link}
                                                                href={`/calculations/${calculation.id}/edit`}
                                                                icon={
                                                                    PencilIcon
                                                                }
                                                            >
                                                                Wijzigen
                                                            </MenuItem>

                                                            <MenuSeparator />
                                                        </>
                                                    )}
                                                    {auth?.permissions?.includes(
                                                        'delete calculations'
                                                    ) && (
                                                        <MenuItem
                                                            className='text-red-600'
                                                            onClick={() =>
                                                                confirmDeleteOrRestore(
                                                                    calculation
                                                                )
                                                            }
                                                            icon={TrashIcon}
                                                        >
                                                            Verwijderen
                                                        </MenuItem>
                                                    )}
                                                </MenuItems>
                                            </Menu>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>

                <SegmentFooter>
                    <CustomPagination
                        meta={calculations?.meta}
                        only={['calculations']}
                    />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default IndexView;
